/**
 * API Configuration
 * Centralized configuration for all API endpoints
 */

// Get the base API URL from environment variables with fallback
const getApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;

  // Fallback to localhost for development if not set to
  if (!envUrl) {
    console.warn('REACT_APP_API_BASE_URL not set, using localhost fallback');
    return 'http://localhost:5000';
  }
  return envUrl;
};

// Base API URL for existing backend services
export const API_BASE_URL = getApiBaseUrl();

// AI Services run on local Flask backend during development
const AI_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// Automation Insights targets its own service (topsreliabilityframework-automationapi),
// not API_BASE_URL. No localhost fallback — REACT_APP_AUTOMATION_API_BASE_URL must be
// set (Vault frontendsecrets in deployed envs).
//
// Deliberately NOT called at module scope (2026-09-04, Copilot review on the
// prod PR): this file is the shared, central API config imported all over
// the app, not just by Automation Insights. Calling this eagerly here used
// to mean ANY environment missing this one Vault key — a local dev machine,
// a preview build, a future service — would throw during module evaluation
// and crash the entire app for every route, not just Automation Insights.
// AUTOMATION_INSIGHTS_ENDPOINTS below now calls this lazily via getters, so
// the throw only ever happens if/when an Automation Insights request is
// actually made, not on import.
const getAutomationApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_AUTOMATION_API_BASE_URL;
  if (!envUrl) {
    throw new Error('REACT_APP_AUTOMATION_API_BASE_URL is not set');
  }
  return envUrl;
};

// API Endpoints Configuration
export const API_ENDPOINTS = {
  // Reliability Framework
  RELIABILITY_FRAMEWORK: {
    PORTFOLIO_OVERVIEW: `${API_BASE_URL}/api/reliability-framework/portfolio-overview`,
    PARAMETERS: `${API_BASE_URL}/api/reliability-framework/parameters`,
    ALL_APPS: `${API_BASE_URL}/api/reliability-framework/portfolio-overview`,
    FULLSTACK_MONITORING_PROGRESS: `${API_BASE_URL}/api/reliability-framework/fullstack-monitoring-progress`, // NEW: crawl/walk/run/monitoring progress
    PLATFORM_PENDING_ITEMS: `${API_BASE_URL}/api/reliability-framework/platform-pending-items`, // NEW: reliability action items by platform
  },

  // Resiliency MSA Assessment
  RESILIENCY_MSA: {
    ALL: `${API_BASE_URL}/api/resiliency-msa`,
    BY_APP: (appName: string) => `${API_BASE_URL}/api/resiliency-msa?applicationName=${encodeURIComponent(appName)}`,
  },

  // Resiliency Framework — commented out, replaced by Resiliency MSA Assessment
  // RESILIENCY_FRAMEWORK: {
  //   PORTFOLIO_OVERVIEW: `${API_BASE_URL}/api/resiliency-framework/portfolio-overview`,
  //   PARAMETERS: `${API_BASE_URL}/api/resiliency-framework/parameters`,
  //   UPDATE_PARAMETER: `${API_BASE_URL}/api/resiliency-framework/update-parameter`,
  //   FULLSTACK_RESILIENCY_PROGRESS: `${API_BASE_URL}/api/resiliency-framework/fullstack-resiliency-progress`,
  //   APPLICATION: (referenceId: string, parameterName?: string) =>
  //     `${API_BASE_URL}/api/resiliency-framework/application/${referenceId}${parameterName ? `?parameter_name=${parameterName}` : ''}`,
  //   AUDIT_HISTORY: (referenceId: string, parameterName?: string) =>
  //     `${API_BASE_URL}/api/resiliency-framework/audit-history/${referenceId}${parameterName ? `?parameter_name=${parameterName}` : ''}`,
  // },

  // Parameter Links
  PARAMETER_LINKS: (referenceId: string) => `${API_BASE_URL}/api/parameter-links/${referenceId}`,

  // Parameter Updates
  UPDATE_PARAMETER: `${API_BASE_URL}/api/update-parameter`,

  // Audit History
  AUDIT_HISTORY: (referenceId: string) => `${API_BASE_URL}/api/reliability-framework/audit-history/${referenceId}`,
  CREATE_AUDIT_ENTRY: `${API_BASE_URL}/api/reliability-framework/audit-history`,

  // Incidents
  INCIDENTS: {
    BASE: `${API_BASE_URL}/api/incidents`,
    P1_P2: `${API_BASE_URL}/api/p1-p2-incidents`,
    P3_P4: `${API_BASE_URL}/api/p3-p4-incidents`,
    P1_P2_WITH_TRENDS: `${API_BASE_URL}/api/p1-p2-incidents-with-trends`,
    P3_P4_WITH_TRENDS: `${API_BASE_URL}/api/p3-p4-incidents-with-trends`,
    MTTR: `${API_BASE_URL}/api/inc-mttr`,
    P1P2_BY_REGION: `${API_BASE_URL}/api/p1p2-by-region`,
  },

  // Hedwig Analysis
  HEDWIG: {
    COMPREHENSIVE_ANALYSIS: `${API_BASE_URL}/api/hedwig/comprehensive-analysis`,
  },

  // AI Analysis (Local Flask Backend)
  AI: {
    ANALYZE_ALERTS: `${AI_BASE_URL}/api/ai/analyze-alerts`,
    HEALTH: `${AI_BASE_URL}/api/ai/health`,
    CONFIG: `${AI_BASE_URL}/api/ai/config`,
  },

  // SNR Alert Insights
  SNR: {
    ALERT_INSIGHTS: `${API_BASE_URL}/api/snr/alert-insights`,
    ALERT_INSIGHTS_BY_APP: (appId: string) => `${API_BASE_URL}/api/snr/alert-insights/${encodeURIComponent(appId)}`,
  },

  // Portfolio Health
  PORTFOLIO_HEALTH: {
    ALL_APPS_SLO_INFO: `${API_BASE_URL}/api/fetch-all-apps-slo-info`,
  },

  // DCO / Decommissioning Cost
  DCO: {
    LOAD_CSV: `${API_BASE_URL}/api/reliability-framework/dco/load-csv`,
    CANCEL_LOAD: `${API_BASE_URL}/api/reliability-framework/dco/cancel-load`,
  },

  // Operational Excellence - Postmortem Actions
  OPERATIONAL_EXCELLENCE: {
    POSTMORTEM_ACTIONS: {
      SUMMARY: `${API_BASE_URL}/api/operational-excellence/postmortem-actions/summary`,
      BY_APPLICATION: `${API_BASE_URL}/api/operational-excellence/postmortem-actions/by-application`,
      LIST: `${API_BASE_URL}/api/operational-excellence/postmortem-actions`,
    },
    POSTMORTEM_STATUS: {
      SUMMARY: `${API_BASE_URL}/api/operational-excellence/postmortem-status/summary`,
    },
  },

  // Jira Integration
  JIRA: {
    HIERARCHY: `${API_BASE_URL}/api/jira/hierarchy`,
  },

  // Postmortem APIs (Maersk API Gateway)
  POSTMORTEM: {
    // Frontend should call backend proxy endpoints (backend will fetch from Maersk)
    OAUTH_TOKEN: `${API_BASE_URL}/api/postmortem/oauth-token`,
    ALL_REPORTS: `${API_BASE_URL}/api/postmortem/all`,
    ENHANCEMENTS: `${API_BASE_URL}/api/postmortem/enhancements`,
  },

  // SpringPM Proxy (DEPRECATED - Use POSTMORTEM instead)
  SPRINGPM: {
    ALL_REPORTS: `${API_BASE_URL}/api/proxy/springpm-all`,
  },

  // SRE Page 0 Metrics
  PAGE0_METRICS: {
    WEEKLY: `${API_BASE_URL}/api/page0-metrics`,
  },

  // MVC App View
  MVC_VIEW: {
    APPLICATIONS: `${API_BASE_URL}/api/mvc-view/applications`,
    APPLICATION: (appId: string) => `${API_BASE_URL}/api/mvc-view/application/${appId}`,
    PORTFOLIOS: `${API_BASE_URL}/api/mvc-view/portfolios`,
    PLATFORMS: `${API_BASE_URL}/api/mvc-view/platforms`,
    PLATFORM_PORTFOLIOS: `${API_BASE_URL}/api/mvc-view/platform-portfolios`,
    STATISTICS: `${API_BASE_URL}/api/mvc-view/statistics`,
  },

  // KPI Targets & Forecast (Incident Ops View)
  KPI_TARGETS: {
    BASE: `${API_BASE_URL}/api/kpi-targets`,
    BASE_PUT: `${API_BASE_URL}/api/kpi-targets/base`,
    OVERRIDE: `${API_BASE_URL}/api/kpi-targets/override`,
    AUDIT: `${API_BASE_URL}/api/kpi-targets/audit`,
    OVERRIDE_DELETE: (metricId: string, periodType: string, periodKey: string, changedBy?: string) =>
      `${API_BASE_URL}/api/kpi-targets/override/${encodeURIComponent(metricId)}/${encodeURIComponent(periodType)}/${encodeURIComponent(periodKey)}${changedBy ? `?changed_by=${encodeURIComponent(changedBy)}` : ''}`,
  },
} as const;

// Kept out of API_ENDPOINTS because it targets AUTOMATION_API_BASE_URL.
// Every value below is a getter, not a plain string — each one calls
// getAutomationApiBaseUrl() at ACCESS time, not at module-load time (see
// the note on getAutomationApiBaseUrl above for why). Shape is unchanged
// for callers: AUTOMATION_INSIGHTS_ENDPOINTS.PANELS.DATA etc. still reads
// exactly the same, so nothing downstream needs to change.
export const AUTOMATION_INSIGHTS_ENDPOINTS = {
  get BASE_URL(): string {
    return getAutomationApiBaseUrl();
  },
  PANELS: {
    get PERFORMANCE_ALERTS(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/panels/performance-alerts`;
    },
    get PLATFORM_SUMMARY(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/panels/platform-table`;
    },
    get PLATFORM_PERFORMANCE(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/panels/platform-summary`;
    },
    get ASSIGNMENT_GROUPS(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/panels/assignment-groups`;
    },
    get PLATFORM_TABLE_GROUPS(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/panels/platform-table/groups`;
    },
    get AUTOMATION_BREAKDOWN(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/panels/automation-breakdown`;
    },
    get PLATFORM_TABLE_TICKETS(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/panels/platform-table/tickets`;
    },
  },
  get DATA(): string {
    return `${getAutomationApiBaseUrl()}/automation/api/data`;
  },
  VISITS: {
    get PING(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/visits/ping`;
    },
    get BEAT(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/visits/beat`;
    },
    get LEADERBOARD(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/visits/leaderboard`;
    },
  },
};

export const SAP_INSIGHTS_ENDPOINTS = {
  get BASE_URL(): string {
    return getAutomationApiBaseUrl();
  },
  PANELS: {
    get PLATFORM_TABLE(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/panels/sap-platform-table`;
    },
    get PLATFORM_TABLE_GROUPS(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/panels/sap-platform-table/groups`;
    },
    get PLATFORM_TABLE_TICKETS(): string {
      return `${getAutomationApiBaseUrl()}/automation/api/panels/sap-platform-table/tickets`;
    },
  },
};

// Export individual endpoints for convenience
export const {
  RELIABILITY_FRAMEWORK,
  // RESILIENCY_FRAMEWORK,  // Commented out — replaced by Resiliency MSA Assessment
  RESILIENCY_MSA,
  PARAMETER_LINKS,
  UPDATE_PARAMETER,
  INCIDENTS,
  HEDWIG,
  SNR,
  AI,
  PORTFOLIO_HEALTH,
  DCO,
  OPERATIONAL_EXCELLENCE,
  JIRA,
  POSTMORTEM,
  SPRINGPM,
  MVC_VIEW,
  KPI_TARGETS,
} = API_ENDPOINTS;

// Log the current API configuration in development
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API Configuration loaded:', {
    baseUrl: API_BASE_URL,
    aiBaseUrl: AI_BASE_URL,
    environment: process.env.NODE_ENV,
  });
}
